import {
  BookingBookingStatus,
  PaymentStatus,
  type Booking,
  type Product,
  type SessionSession
} from '@linktivity/ars-neo';
import { pushGa4Event } from './dataLayer';
import {
  bookingToGa4Ecommerce,
  checkoutToGa4Ecommerce,
  productToGa4Ecommerce,
  type Ga4Checkout,
  type Ga4TrackOptions
} from './items';
import { safely } from './safely';

const SENT_EVENTS_KEY = 'ga4_sent_events';
const SENT_EVENTS_LIMIT = 40;

/**
 * Transactions already reported. sessionStorage so a refresh or a
 * back-navigation to the result page cannot double-count a purchase; the
 * in-memory mirror covers the browsers that refuse storage access outright,
 * where the read would otherwise always come back empty.
 */
const sentInThisDocument = new Set<string>();

function readSentEvents(): string[] {
  try {
    const raw = window.sessionStorage.getItem(SENT_EVENTS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((key): key is string => typeof key === 'string')
      : [];
  } catch {
    return [];
  }
}

function hasSent(key: string): boolean {
  return sentInThisDocument.has(key) || readSentEvents().includes(key);
}

function rememberSent(key: string): void {
  sentInThisDocument.add(key);
  try {
    const next = [...readSentEvents().filter(id => id !== key), key].slice(
      -SENT_EVENTS_LIMIT
    );
    window.sessionStorage.setItem(SENT_EVENTS_KEY, JSON.stringify(next));
  } catch {
    // Private-mode storage failures must not block the event.
  }
}

/**
 * Product detail body on screen. The main product only, and no money: the user
 * has not chosen a plan, a unit or a quantity yet.
 */
export function trackGa4ViewItem(product: Product, uiLanguage?: string): void {
  safely(() => {
    const { agentId, ecommerce } = productToGa4Ecommerce(product);
    const item = ecommerce.items[0];
    // Both come off the loaded product, so an empty one means the body on
    // screen is not yet the product this event would claim.
    if (!item?.item_id || !item.item_name) return;
    pushGa4Event({ event: 'view_item', agentId, ecommerce, uiLanguage });
  });
}

/**
 * The user committed to a plan, units and options. Keyed to a successful session
 * PUT and built from its response — never to the button click or the navigation
 * that follows, so an abandoned or rejected submit reports nothing. One event
 * per successful PUT: re-submitting after edits is a new PUT, so a new event.
 *
 * Despite the name this is not about the cart feature, which most sites do not
 * enable; it is the one "proceed to order" step every site has.
 */
export function trackGa4AddToCart(
  session: SessionSession,
  options: Ga4TrackOptions
): void {
  safely(() => {
    const { agentId, ecommerce } = checkoutToGa4Ecommerce(
      { id: session.id ?? '', sessions: [session] },
      options
    );
    if (ecommerce.items.length === 0) return;
    pushGa4Event({
      event: 'add_to_cart',
      agentId,
      ecommerce,
      uiLanguage: options.uiLanguage
    });
  });
}

/**
 * The confirm page displayed the checkout it fetched. Every display counts as a
 * new checkout entry — a reload or a return trip through the product page is a
 * new one even on an unchanged session id — so de-duping within one display is
 * the caller's job, and this sender deliberately keeps no cross-visit memory.
 */
export function trackGa4BeginCheckout(
  checkout: Ga4Checkout,
  options: Ga4TrackOptions
): void {
  safely(() => {
    if (!checkout.id) return;
    const { agentId, ecommerce } = checkoutToGa4Ecommerce(checkout, options);
    if (ecommerce.items.length === 0) return;
    pushGa4Event({
      event: 'begin_checkout',
      agentId,
      ecommerce,
      uiLanguage: options.uiLanguage
    });
  });
}

/**
 * Linkpay redirect URL obtained, about to leave ARS. `onSent` runs exactly once
 * on every path — empty, pushed, or thrown — because the caller gates the
 * payment navigation on it.
 */
export function trackGa4PaymentRedirect(
  checkout: Ga4Checkout,
  options: Ga4TrackOptions,
  onSent: () => void
): void {
  safely(() => {
    const { agentId, ecommerce } = checkoutToGa4Ecommerce(checkout, options);
    if (ecommerce.items.length === 0) {
      onSent();
      return;
    }
    pushGa4Event({
      event: 'payment_redirect',
      agentId,
      ecommerce,
      paymentProvider: 'linkpay',
      uiLanguage: options.uiLanguage,
      onSent
    });
  }, onSent);
}

/**
 * The order the backend settled. Safe to call on every poll tick: it reports at
 * most once per transaction, and only with a transaction id, a currency, an
 * amount and items — a conversion that cannot be reconciled with the order is
 * not reported at all.
 */
/**
 * The transaction this booking should report, or null when it is not a settled,
 * unreported sale. Cheap and synchronous on purpose: the result page polls every
 * few seconds, so callers check this before paying for anything expensive.
 */
function pendingPurchase(
  booking: Booking
): { transactionId: string; key: string } | null {
  // A sale the backend has settled and been paid for. Anything short of both
  // is not a purchase: the result page keeps polling, and a booking that never
  // gets there reports nothing.
  if (booking.bookingStatus !== BookingBookingStatus.BookingStatusConfirmed) {
    return null;
  }
  if (booking.payment?.status !== PaymentStatus.PaymentStatusPaid) return null;

  // The ARS reservation number (予約番号) — the id the confirmation page shows
  // the user and the rest of the company keys on, not the internal booking id.
  const transactionId = booking.bookingConfirmation?.externalBooking?.id;
  if (!transactionId) return null;

  // Checked before mapping: the de-dupe key needs no ecommerce payload.
  const key = `purchase:${transactionId}`;
  if (hasSent(key)) return null;

  return { transactionId, key };
}

/**
 * The order the backend settled. Safe to call on every poll tick: it reports at
 * most once per transaction, and only once the backend says the sale is paid.
 */
export function trackGa4Purchase(
  booking: Booking,
  options: Ga4TrackOptions
): void {
  safely(() => {
    const pending = pendingPurchase(booking);
    if (!pending) return;
    const { transactionId, key } = pending;

    const { agentId, ecommerce } = bookingToGa4Ecommerce(booking, options);
    if (
      !ecommerce.currency ||
      ecommerce.value === undefined ||
      ecommerce.items.length === 0
    ) {
      return;
    }

    // Push first: a throw here must leave the key unrecorded so a later poll
    // can retry, rather than suppressing the purchase permanently.
    pushGa4Event({
      event: 'purchase',
      agentId,
      ecommerce: { ...ecommerce, transaction_id: transactionId },
      uiLanguage: options.uiLanguage
    });
    rememberSent(key);
  });
}
